import CellJson from "../api/json/CellJson"
import {
    AnyTemplateJson,
    AnyTemplateParts,
    TemplateChatParts,
    TemplateStandardParts,
} from "../api/json/TemplateJson"
import {TemplateType} from "../api/enum/TemplateType"
import {AnyColumnJson} from "../api/json/ColumnJson"

export type AnyTemplateDataSource = CellJson[] | Record<string, string>

export default class TemplateUtil {
    static inDoubleBrackets(string: string): string {
        return `{{${string}}}`
    }

    static renderChat(chatProperties: TemplateChatParts, dataSource: AnyTemplateDataSource, allColumns?: AnyColumnJson[]): {system: string, user: string, assistant: string} {
        return {
            system: this.renderAny(chatProperties.system, dataSource, allColumns),
            user: this.renderAny(chatProperties.user, dataSource, allColumns),
            assistant: this.renderAny(chatProperties.assistant, dataSource, allColumns),
        }
    }

    static renderStandard(standardProperties: TemplateStandardParts, dataSource: AnyTemplateDataSource, allColumns?: AnyColumnJson[]): {prompt: string, completion: string} {
        return {
            prompt: this.renderAny(standardProperties.prompt, dataSource, allColumns),
            completion: this.renderAny(standardProperties.completion, dataSource, allColumns),
        }
    }

    static renderCompletionEquivalent(template: AnyTemplateJson, dataSource: AnyTemplateDataSource, allColumns?: AnyColumnJson[]): string {
        if (template.type === TemplateType.CHAT) {
            return this.renderAny(template.assistant, dataSource, allColumns)
        }
        return this.renderAny(template.completion, dataSource, allColumns)
    }

    static renderAny = (template: string, dataSource: AnyTemplateDataSource, allColumns?: AnyColumnJson[]): string => {
        let defaultValues: Record<string, string> = {}
        allColumns?.forEach(column => {
            defaultValues[column.reference] = "" // TODO: Could be a default value on the column
        })

        if (Array.isArray(dataSource) && dataSource.length > 0 && 'column' in dataSource[0]) { // Cells
            (dataSource as CellJson[]).forEach(cell => {
                defaultValues[cell.column.reference] = cell.value
            })
        } else if (TemplateUtil.isRecordStringString(dataSource)) { // Record
            Object.entries(dataSource).forEach(([key, value]) => {
                defaultValues[key] = value
            })
        } else {
            return "Unsupported data source"
        }

        let output = template
        Object.entries(defaultValues).forEach(([key, value]) => {
            output = output.replaceAll(TemplateUtil.inDoubleBrackets(key), value)
        })
        return output
    }

    private static isRecordStringString(obj: any): obj is Record<string, string> {
        if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
            return false
        }

        for (const [, value] of Object.entries(obj)) {
            if (typeof value !== 'string') {
                return false
            }
        }

        return true
    }

    static findPromptReferences = (properties: AnyTemplateParts): string[] => {
        try {
            switch (properties.type) {
                case TemplateType.CHAT:
                    return [
                        ...TemplateUtil.extractReferencesFromString(properties.system),
                        ...TemplateUtil.extractReferencesFromString(properties.user),
                    ]
                case TemplateType.STANDARD:
                    return TemplateUtil.extractReferencesFromString(properties.prompt)
            }
        } catch(error) {
            console.error(error)
        }
        return []
    }

    static findCompletionReferences = (properties: AnyTemplateParts): string[] => {
        try {
            switch (properties.type) {
                case TemplateType.CHAT:
                    return TemplateUtil.extractReferencesFromString(properties.assistant)
                case TemplateType.STANDARD:
                    return TemplateUtil.extractReferencesFromString(properties.completion)
            }
        } catch(error) {
            console.error(error)
        }
        return []
    }

    static findAllReferences = (properties: AnyTemplateParts): string[] => {
        return [
            ...TemplateUtil.findPromptReferences(properties),
            ...TemplateUtil.findCompletionReferences(properties)
        ]
    }

    static isColumnReferenced = (column: AnyColumnJson, template: AnyTemplateJson): boolean => {
        return TemplateUtil.findAllReferences(template).includes(column.reference)
    }

    static extractReferencesFromString = (template: string): string[] => {
        const regex = /{{(.*?)}}/g;
        let matches = [];
        let match;

        while ((match = regex.exec(template)) !== null) {
            matches.push(match[1]);
        }

        return matches;
    }
}